import React from "react";

import {models} from "@reactivated";

export type EntryLocator = string;
type EntryInfo = models["journal.predictions.EntryInfo"];
type RootPatterns = models["journal.predictions.RootPatterns"];

// ============================================================================
// Types
// ============================================================================

type DateValue = string; // ISO date string (YYYY-MM-DD)
type SessionMap = Record<DateValue, number>; // date -> session index (0 = most recent)

interface PatternWithSession {
    locators: Set<EntryLocator>;
    lastSession: number;
    frequency: number; // Number of dates this pattern occurred
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Build a session map from all dates across all patterns.
 * Each unique date gets an index: 0 = most recent, 1 = next, etc.
 */
function buildSessionMap(roots: RootPatterns[]): SessionMap {
    const allDates = [
        ...new Set(roots.flatMap((r) => r.patterns.flatMap((p) => p.dates))),
    ].sort((a, b) => b.localeCompare(a)); // Descending (most recent first)

    return Object.fromEntries(allDates.map((date, idx) => [date, idx]));
}

/**
 * Convert raw patterns to enriched patterns with session info.
 */
function enrichPatterns(
    root: RootPatterns,
    sessionMap: SessionMap,
): PatternWithSession[] {
    return root.patterns.map((pattern) => {
        const sessionIndices = pattern.dates.map((d) => sessionMap[d]);
        return {
            locators: new Set(pattern.locators),
            lastSession: Math.min(...sessionIndices),
            frequency: pattern.dates.length,
        };
    });
}

/**
 * Calculate recency weight using exponential decay.
 * halfLife = number of sessions until weight halves.
 */
function recencyWeight(session: number, halfLife = 5): number {
    return Math.exp((-Math.log(2) * session) / halfLife);
}

// ============================================================================
// Root Ordering Algorithm
// ============================================================================

/**
 * Order roots by "usefulness" for prediction, avoiding redundant entry points.
 *
 * Uses greedy selection to pick diverse roots - if cereal is already picked,
 * yogurt and collagen (which lead to the same meals) are penalized.
 *
 * Score = pattern_count × recency_weight × redundancy_penalty
 */
function getOrderedRoots(
    roots: RootPatterns[],
    sessionMap: SessionMap,
): EntryLocator[] {
    // Build pattern data for each root
    const rootData = roots.map((root) => {
        const patterns = enrichPatterns(root, sessionMap);
        const bestSession =
            patterns.length > 0
                ? Math.min(...patterns.map((p) => p.lastSession))
                : Infinity;

        // Collect all items that appear with this root
        const companions = new Set<EntryLocator>();
        for (const pattern of patterns) {
            for (const locator of pattern.locators) {
                if (locator !== root.root_locator) {
                    companions.add(locator);
                }
            }
        }

        // Recency is the primary factor - half-life of 5 sessions
        const recencyScore = Math.exp((-Math.log(2) * bestSession) / 5);

        // Pattern count is a minor tiebreaker (adds 0-0.5 to score)
        const patternBonus = Math.min(0.5, Math.log2(patterns.length + 1) / 20);

        // Score is primarily recency, with patterns as tiebreaker
        const baseScore = recencyScore + patternBonus;

        return {
            locator: root.root_locator,
            companions,
            baseScore,
            patternCount: patterns.length,
        };
    });

    // Sort by base score
    rootData.sort((a, b) => b.baseScore - a.baseScore);

    return rootData.map((r) => r.locator);
}

// ============================================================================
// Common Cores Algorithm
// ============================================================================

/**
 * Find common companion foods ("cores") for a root.
 *
 * Algorithm uses greedy selection for branch diversity:
 * 1. Build co-occurrence data for all items
 * 2. Select items that represent DISTINCT meal branches
 * 3. Boost items that rarely co-occur with already selected cores
 *
 * This ensures we get representatives from different meal types
 * (e.g., both yogurt/breakfast AND milk/dinner branches)
 */
function getCommonCores(root: RootPatterns, sessionMap: SessionMap): EntryLocator[] {
    const rootLocator = root.root_locator;
    const patterns = enrichPatterns(root, sessionMap);

    if (patterns.length < 2) {
        return [];
    }

    // Build item stats and co-occurrence matrix
    const itemStats = new Map<
        EntryLocator,
        {
            patternCount: number;
            weightedScore: number;
            bestSession: number;
            patternIndices: Set<number>; // Which patterns this item appears in
        }
    >();

    for (let i = 0; i < patterns.length; i++) {
        const pattern = patterns[i];
        const weight = recencyWeight(pattern.lastSession) * pattern.frequency;

        for (const locator of pattern.locators) {
            if (locator === rootLocator) continue;

            const existing = itemStats.get(locator);
            if (existing != null) {
                existing.patternCount += 1;
                existing.weightedScore += weight;
                existing.bestSession = Math.min(
                    existing.bestSession,
                    pattern.lastSession,
                );
                existing.patternIndices.add(i);
            } else {
                itemStats.set(locator, {
                    patternCount: 1,
                    weightedScore: weight,
                    bestSession: pattern.lastSession,
                    patternIndices: new Set([i]),
                });
            }
        }
    }

    // Filter to items with at least 2 distinct patterns AND recent usage
    const candidates = [...itemStats.entries()]
        .filter(([, stats]) => stats.patternCount >= 2 && stats.bestSession <= 50)
        .map(([locator, stats]) => ({
            locator,
            ...stats,
        }));

    if (candidates.length === 0) {
        return [];
    }

    // Sort by weighted score to find dominant items
    const byWeightedScore = [...candidates].sort(
        (a, b) => b.weightedScore - a.weightedScore,
    );
    const topByWeight = byWeightedScore[0];

    // First, find the two best "branch representatives" - items with high pattern count
    // that have LOW mutual overlap (they represent different meal types)
    const topCandidates = [...candidates]
        .sort((a, b) => b.patternCount - a.patternCount)
        .slice(0, 20); // Consider top 20 by pattern count

    // Find the pair with lowest overlap ratio and decent combined pattern count
    let bestPair: [(typeof candidates)[0], (typeof candidates)[0]] | null = null;
    let bestPairScore = -Infinity;

    for (let i = 0; i < topCandidates.length; i++) {
        for (let j = i + 1; j < topCandidates.length; j++) {
            const a = topCandidates[i];
            const b = topCandidates[j];

            // Calculate overlap: how many patterns contain BOTH items
            let overlap = 0;
            for (const idx of a.patternIndices) {
                if (b.patternIndices.has(idx)) overlap++;
            }
            const overlapRatio =
                overlap / Math.min(a.patternIndices.size, b.patternIndices.size);

            // Score: reward low overlap and high combined pattern count
            // Exclusivity bonus is (1 - overlapRatio)^2 to heavily favor mutual exclusivity
            const exclusivityBonus = Math.pow(1 - overlapRatio, 2);
            const combinedPatterns = a.patternCount + b.patternCount;
            const score = combinedPatterns * exclusivityBonus;

            if (score > bestPairScore) {
                bestPairScore = score;
                bestPair = [a, b];
            }
        }
    }

    // Start with the best branch pair
    const selected: EntryLocator[] = [];
    const coveredPatterns = new Set<number>();

    if (bestPair != null) {
        // Check if there's a dominant item by weighted score that's not in the pair
        // If top item has >20x the weighted score of the lower pair member, use it instead
        const lowerPairWeight = Math.min(
            bestPair[0].weightedScore,
            bestPair[1].weightedScore,
        );
        const topIsInPair =
            topByWeight.locator === bestPair[0].locator ||
            topByWeight.locator === bestPair[1].locator;

        if (!topIsInPair && topByWeight.weightedScore > lowerPairWeight * 20) {
            // Dominant item exists outside the pair - put it first, keep higher pair member second
            const higherPairMember =
                bestPair[0].weightedScore >= bestPair[1].weightedScore
                    ? bestPair[0]
                    : bestPair[1];
            selected.push(topByWeight.locator, higherPairMember.locator);
            for (const idx of topByWeight.patternIndices) coveredPatterns.add(idx);
            for (const idx of higherPairMember.patternIndices) coveredPatterns.add(idx);
        } else {
            // Use the pair as-is, ordered by weighted score
            const [first, second] =
                bestPair[0].weightedScore >= bestPair[1].weightedScore
                    ? bestPair
                    : [bestPair[1], bestPair[0]];

            selected.push(first.locator, second.locator);
            for (const idx of first.patternIndices) coveredPatterns.add(idx);
            for (const idx of second.patternIndices) coveredPatterns.add(idx);
        }
    }

    const remaining = new Set(candidates.map((c) => c.locator));
    for (const loc of selected) remaining.delete(loc);

    // Helper: calculate overlap ratio between an item and already selected items
    const getOverlapRatio = (item: (typeof candidates)[0]): number => {
        if (coveredPatterns.size === 0) return 0;
        let overlap = 0;
        for (const idx of item.patternIndices) {
            if (coveredPatterns.has(idx)) overlap++;
        }
        return overlap / item.patternIndices.size;
    };

    // Fill remaining slots using diversity-aware greedy selection
    while (selected.length < 6 && remaining.size > 0) {
        let bestCandidate: (typeof candidates)[0] | null = null;
        let bestScore = -Infinity;

        for (const candidate of candidates) {
            if (!remaining.has(candidate.locator)) continue;

            const overlapRatio = getOverlapRatio(candidate);
            const diversityBonus = 1 - overlapRatio * 0.5;
            const score =
                candidate.patternCount *
                diversityBonus *
                (1 + candidate.weightedScore / 100);

            if (score > bestScore) {
                bestScore = score;
                bestCandidate = candidate;
            }
        }

        if (bestCandidate == null) break;

        selected.push(bestCandidate.locator);
        remaining.delete(bestCandidate.locator);
        for (const idx of bestCandidate.patternIndices) {
            coveredPatterns.add(idx);
        }
    }

    // Keep the branch pair (first 2) in order by weighted score,
    // then sort remaining cores (positions 3-6) by weighted score
    const candidateMap = new Map(candidates.map((c) => [c.locator, c]));
    const getWeightedScore = (loc: EntryLocator) =>
        candidateMap.get(loc)?.weightedScore ?? 0;

    // Sort the branch pair (first 2) by weighted score
    if (selected.length >= 2) {
        const [first, second] = selected;
        if (getWeightedScore(second) > getWeightedScore(first)) {
            selected[0] = second;
            selected[1] = first;
        }
    }

    // Sort remaining cores (positions 3+) by weighted score
    if (selected.length > 2) {
        const remaining = selected
            .slice(2)
            .sort((a, b) => getWeightedScore(b) - getWeightedScore(a));
        for (let i = 0; i < remaining.length; i++) {
            selected[i + 2] = remaining[i];
        }
    }

    return selected;
}

// ============================================================================
// Suggestions Algorithm
// ============================================================================

/**
 * Get suggested items based on selected root and cores.
 *
 * Algorithm:
 * 1. Include ALL items from entries (nothing hidden)
 * 2. Score by frequency × recency, with a boost for patterns containing selected cores
 * 3. Items that don't appear with the root at all get a minimal score (appear at bottom)
 * 4. Return items sorted by score, excluding root and common cores
 *
 * Items matching selected cores get bumped up, but all items still appear.
 */
function getSuggestedItems(
    root: RootPatterns,
    sessionMap: SessionMap,
    selectedCores: EntryLocator[],
    commonCores: EntryLocator[],
    allEntries: Record<EntryLocator, unknown>,
    options: {boostSelectedCores: boolean} = {boostSelectedCores: true},
): EntryLocator[] {
    const rootLocator = root.root_locator;
    const patterns = enrichPatterns(root, sessionMap);
    const excluded = new Set([rootLocator, ...commonCores]);
    const selectedCoresSet = new Set(selectedCores);

    // Score each non-excluded item across ALL patterns
    const itemScores = new Map<
        EntryLocator,
        {
            baseScore: number;
            matchingScore: number;
            patternCount: number;
            bestSession: number;
        }
    >();

    for (const pattern of patterns) {
        const weight = recencyWeight(pattern.lastSession) * pattern.frequency;

        // Check if this pattern contains ALL selected cores
        const matchesSelectedCores =
            selectedCores.length === 0 ||
            selectedCores.every((core) => pattern.locators.has(core));

        for (const locator of pattern.locators) {
            if (excluded.has(locator)) continue;
            if (selectedCoresSet.has(locator)) continue;

            const existing = itemScores.get(locator);
            if (existing != null) {
                existing.baseScore += weight;
                if (matchesSelectedCores) {
                    existing.matchingScore += weight;
                }
                existing.patternCount += 1;
                existing.bestSession = Math.min(
                    existing.bestSession,
                    pattern.lastSession,
                );
            } else {
                itemScores.set(locator, {
                    baseScore: weight,
                    matchingScore: matchesSelectedCores ? weight : 0,
                    patternCount: 1,
                    bestSession: pattern.lastSession,
                });
            }
        }
    }

    // Add all entries that don't appear with the root (score = 0)
    for (const locator of Object.keys(allEntries)) {
        if (excluded.has(locator)) continue;
        if (selectedCoresSet.has(locator)) continue;
        if (!itemScores.has(locator)) {
            itemScores.set(locator, {
                baseScore: 0,
                matchingScore: 0,
                patternCount: 0,
                bestSession: Infinity,
            });
        }
    }

    // Convert to array and sort
    const candidates = [...itemScores.entries()].map(([locator, stats]) => ({
        locator,
        // Final score: matching patterns get 10x boost (if enabled), plus base score
        score: options.boostSelectedCores
            ? stats.matchingScore * 10 + stats.baseScore
            : stats.baseScore,
        ...stats,
    }));

    // Sort by score, then by recency
    candidates.sort((a, b) => {
        if (b.score !== a.score) {
            return b.score - a.score;
        }
        return a.bestSession - b.bestSession;
    });

    return candidates.map((c) => c.locator);
}

/**
 * Get all other items that have appeared with the root but didn't qualify
 * for commonCores or suggestedItems.
 */
function getEverythingElse(
    root: RootPatterns,
    sessionMap: SessionMap,
    commonCores: EntryLocator[],
    suggestedItems: EntryLocator[],
): EntryLocator[] {
    const rootLocator = root.root_locator;
    const patterns = enrichPatterns(root, sessionMap);
    const excluded = new Set([rootLocator, ...commonCores, ...suggestedItems]);

    const itemScores = new Map<EntryLocator, number>();

    for (const pattern of patterns) {
        const weight = recencyWeight(pattern.lastSession) * pattern.frequency;

        for (const locator of pattern.locators) {
            if (excluded.has(locator)) continue;

            itemScores.set(locator, (itemScores.get(locator) ?? 0) + weight);
        }
    }

    // Sort by score descending
    const candidates = [...itemScores.entries()].sort((a, b) => b[1] - a[1]);

    return candidates.map(([locator]) => locator);
}

// ============================================================================
// Hook
// ============================================================================

export function usePredictions(
    options: {
        entries: Record<EntryLocator, EntryInfo>;
        roots: RootPatterns[];
    },
    results: models["journal.nutrition.SearchResult"][],
) {
    const [selectedRootLocator, setSelectedRootLocator] =
        React.useState<EntryLocator | null>(null);
    const [selectedCommonCores, setSelectedCommonCores] = React.useState<
        EntryLocator[]
    >([]);
    const [selectedSuggestedItems, setSelectedSuggestedItems] = React.useState<
        EntryLocator[]
    >([]);
    const [selectedEverythingElse, setSelectedEverythingElse] = React.useState<
        EntryLocator[]
    >([]);

    const sessionMap = React.useMemo(
        () => buildSessionMap(options.roots),
        [options.roots],
    );
    const recent = React.useMemo(
        () => getOrderedRoots(options.roots, sessionMap),
        [options.roots, sessionMap],
    );

    const clearSelections = () => {
        setSelectedRootLocator(null);
        setSelectedCommonCores([]);
        setSelectedSuggestedItems([]);
        setSelectedEverythingElse([]);
    };

    const isRealRoot = (locator: EntryLocator) =>
        options.roots.some((r) => r.root_locator === locator);

    if (selectedRootLocator == null) {
        return {
            clearSelections,
            recent,
            setSelectedRootLocator,
            isRealRoot,
            results: null,
        };
    }

    const selectedRoot = options.roots.find(
        (r) => r.root_locator === selectedRootLocator,
    );

    if (selectedRoot == null) {
        return {
            clearSelections,
            recent,
            setSelectedRootLocator,
            isRealRoot,
            results: {
                isFakeRoot: true,
                toggleCommonCore: () => {},
                toggleSuggestedItem: () => {},
                toggleEverythingElse: (entryLocator: EntryLocator) => {
                    setSelectedEverythingElse((prev) => {
                        if (prev.includes(entryLocator)) {
                            return prev.filter((l) => l !== entryLocator);
                        }
                        return [...prev, entryLocator];
                    });
                },
                entries: options.entries,
                selectedRootLocator,
                commonCores: [],
                suggestedItems: [],
                everythingElse: results
                    .map((r) => `${r.backend_id}:${r.backend}`)
                    .filter((r) => r !== selectedRootLocator),
                selectedLocators: [
                    selectedRootLocator,
                    ...selectedCommonCores,
                    ...selectedSuggestedItems,
                    ...selectedEverythingElse,
                ],
            },
        };
    }

    const commonCores = getCommonCores(selectedRoot, sessionMap);
    const suggestedItems = getSuggestedItems(
        selectedRoot,
        sessionMap,
        selectedCommonCores,
        commonCores,
        options.entries,
    );
    const everythingElse = getEverythingElse(
        selectedRoot,
        sessionMap,
        commonCores,
        suggestedItems,
    );

    return {
        clearSelections,
        setSelectedRootLocator: (locator: EntryLocator | null) => {
            clearSelections();
            setSelectedRootLocator(locator);
        },
        recent,
        isRealRoot,
        results: {
            isFakeRoot: false,
            toggleCommonCore: (locator: EntryLocator) => {
                setSelectedCommonCores((prev) => {
                    if (prev.includes(locator)) {
                        return prev.filter((l) => l !== locator);
                    }
                    return [...prev, locator];
                });
            },
            toggleSuggestedItem: (locator: EntryLocator) => {
                setSelectedSuggestedItems((prev) => {
                    if (prev.includes(locator)) {
                        return prev.filter((l) => l !== locator);
                    }
                    return [...prev, locator];
                });
            },
            toggleEverythingElse: (locator: EntryLocator) => {
                setSelectedEverythingElse((prev) => {
                    if (prev.includes(locator)) {
                        return prev.filter((l) => l !== locator);
                    }
                    return [...prev, locator];
                });
            },
            entries: options.entries,
            selectedRootLocator,
            commonCores: commonCores,
            suggestedItems: suggestedItems,
            everythingElse: everythingElse,
            selectedLocators: [
                selectedRootLocator,
                ...selectedCommonCores,
                ...selectedSuggestedItems,
                ...selectedEverythingElse,
            ],
        },
    };
}

export type PredictionsHandler = ReturnType<typeof usePredictions>;

export type SelectedRootHandler = NonNullable<
    ReturnType<typeof usePredictions>["results"]
>;
